import { ReactNode } from "react";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { ModalConfig, useModalStore } from "@/stores/use-modal-store";
import { cn } from "@/utils/classes";

/* Prop Types */
interface InfoModalTriggerProps {
  modalContent: ReactNode;
  iconClassName?: string;
  className?: string;
  onClick?: () => void;
  config?: ModalConfig;
  children?: ReactNode; // Custom trigger element (if not provided, defaults to icon)
}

/*
 * InfoModalTrigger component
 *
 * A reusable component that displays an information icon (or custom trigger) and opens a modal
 * with the provided content when clicked.
 *
 */
export const InfoModalTrigger = ({ modalContent, iconClassName, className, onClick, config, children }: InfoModalTriggerProps) => {
  /* Hooks */
  const { open } = useModalStore((state) => state.modalActions);

  /* Handlers */
  const handleClick = () => {
    onClick?.();
    open(modalContent, config);
  };

  /* Render */
  return (
    /* Basic html button used because of accessibility reasons */
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        "inline-flex items-center justify-center cursor-pointer transition-colors hover:text-primary",
        {
          "hover:text-primary": !children,
        },
        className,
      )}
      aria-label="Show information"
    >
      {children ? children : <InformationCircleIcon className={cn("size-5", iconClassName)} />}
    </button>
  );
};
